import React, { useState, useRef } from 'react';
import {
  Box,
  HStack,
  VStack,
  Text,
  Badge,
  Icon,
  Tooltip,
  Spinner,
  Menu,
  MenuButton,
  MenuList,
  MenuItem,
  MenuGroup,
  MenuDivider,
  Input,
  InputGroup,
  InputLeftElement,
  useDisclosure,
  useColorModeValue,
} from '@chakra-ui/react';
import { FiPlus, FiBriefcase, FiChevronDown, FiCheck, FiSearch } from 'react-icons/fi';
import moment from 'moment';
import StageBadge from '../../../components/CRM/StageBadge';
import { countOpenDeals, dealDisplayLabel } from './useWorkspaceSearch';
import { CLOSED_STAGES_LEGACY } from '../../../constants/crmConstants';

/**
 * Deal switcher — one dropdown for every deal this person touches.
 *
 * The prominent button shows the CURRENTLY SELECTED deal as loan officers
 * identify it: "{loan #} — {street address}". The dropdown is split into
 * three categories:
 *
 *   Active            — open deals where this person is the borrower
 *   Closed / Canceled — funded/won/lost borrower deals
 *   Party             — deals where this person is a related party (agent,
 *                       co-borrower, title...). Selecting one works it right
 *                       here — so from an agent like Melissa's page you can
 *                       click through every deal you share with her.
 *
 * When the list is long a search box appears at the top: type a borrower
 * name, property address, or loan number to filter all three sections.
 * Enter selects the first match.
 */

const isClosedStage = (stage) => CLOSED_STAGES_LEGACY.includes(stage);

// Show the in-dropdown search whenever there's actually a list to filter
// (hidden for the 90% single-deal case)
const SEARCH_THRESHOLD = 2;

const formatChipAmount = (amount) => {
  if (!amount || amount <= 0) return null;
  if (amount >= 1000000) return `$${(amount / 1000000).toFixed(1)}M`;
  if (amount >= 1000) return `$${Math.round(amount / 1000)}K`;
  return `$${amount}`;
};

const formatRole = (role) =>
  (role || 'related party')
    .split('-')
    .map((w) => w.charAt(0).toUpperCase() + w.slice(1))
    .join(' ');

// Secondary line under the label. Loan # lives in the label, so the subtext
// carries context. Party deals lead with WHOSE deal it is (the deal name is
// the borrower), since the label alone doesn't say.
const dealSubtext = (deal, { isParty = false } = {}) => {
  const parts = [];
  if (isParty && deal.dealName) parts.push(deal.dealName);
  if (deal.product) parts.push(deal.product.toUpperCase());
  if (deal.propertyAddress?.city) parts.push(deal.propertyAddress.city);
  if (!isParty && deal.createdAt) parts.push(`Started ${moment(deal.createdAt).format('MMM D, YYYY')}`);
  return parts.slice(0, 3).join(' · ');
};

// Borrower name (deal name), property address, or loan number
const dealMatchesQuery = (deal, query) => {
  if (!query) return true;
  return [
    deal.dealName,
    deal.loanNumber,
    deal.propertyAddress?.street,
    deal.propertyAddress?.city,
    deal.propertyAddress?.state,
    deal.propertyAddress?.zip,
  ].some((v) => v && String(v).toLowerCase().includes(query));
};

function DealMenuItem({ deal, isSelected, isParty, onSelect }) {
  const mutedColor = useColorModeValue('gray.600', 'gray.400');
  const selectedItemBG = useColorModeValue('blue.50', 'blue.900');
  const sub = dealSubtext(deal, { isParty });

  return (
    <MenuItem onClick={() => onSelect(deal.id)} bg={isSelected ? selectedItemBG : undefined} py={2}>
      <HStack spacing={2} align="flex-start" w="100%">
        <Icon
          as={FiCheck}
          boxSize={3.5}
          mt={1}
          color={isSelected ? 'blue.400' : 'transparent'}
          flexShrink={0}
        />
        <VStack spacing={0.5} align="flex-start" minW={0} flex={1}>
          <HStack spacing={2} minW={0} w="100%">
            <StageBadge stage={deal.stage} />
            <Text fontSize="sm" fontWeight="semibold" noOfLines={1} minW={0}>
              {dealDisplayLabel(deal)}
            </Text>
            {formatChipAmount(deal.amount) && (
              <Text fontSize="xs" fontWeight="bold" color="green.500" flexShrink={0}>
                {formatChipAmount(deal.amount)}
              </Text>
            )}
            {isParty && (
              <Badge colorScheme="cyan" fontSize="2xs" flexShrink={0}>
                {formatRole(deal.relationshipRole)}
              </Badge>
            )}
            {deal.ariveId && (
              <Badge colorScheme="teal" fontSize="2xs" flexShrink={0}>Arive</Badge>
            )}
          </HStack>
          {sub && (
            <Text fontSize="2xs" color={mutedColor} noOfLines={1}>
              {sub}
            </Text>
          )}
        </VStack>
      </HStack>
    </MenuItem>
  );
}

export default function DealSwitcher({
  deals = [],
  relatedPartyDeals = [],
  selectedDealId,
  loading = false,
  onSelect,
  onNewDeal,
  // Header-embedded mode: no outer padding, no "DEALS" label, button fills
  // its grid column. Dropdown behavior (search, categories, party) unchanged.
  compact = false,
}) {
  const buttonBG = useColorModeValue('blue.50', 'blue.900');
  const buttonBorder = useColorModeValue('blue.400', 'blue.300');
  const buttonHoverBG = useColorModeValue('blue.100', 'blue.800');
  const mutedColor = useColorModeValue('gray.600', 'gray.400');
  const newDealColor = useColorModeValue('purple.600', 'purple.300');
  const dashedBorder = useColorModeValue('gray.200', 'gray.600');

  // Controlled menu so the search box can select-and-close on Enter
  const { isOpen, onOpen, onClose } = useDisclosure();
  const [filterText, setFilterText] = useState('');
  const searchInputRef = useRef(null);

  // deals arrive open-first (sortDealsForDisplay); filtering preserves order
  const activeDeals = deals.filter((d) => !isClosedStage(d.stage));
  const closedDeals = deals.filter((d) => isClosedStage(d.stage));
  const hasAnyDeal = deals.length > 0 || relatedPartyDeals.length > 0;
  const totalDeals = deals.length + relatedPartyDeals.length;
  const showSearch = totalDeals >= SEARCH_THRESHOLD;

  const query = filterText.trim().toLowerCase();
  const filteredActive = activeDeals.filter((d) => dealMatchesQuery(d, query));
  const filteredClosed = closedDeals.filter((d) => dealMatchesQuery(d, query));
  const filteredParty = relatedPartyDeals.filter((d) => dealMatchesQuery(d, query));
  const noMatches = query && filteredActive.length + filteredClosed.length + filteredParty.length === 0;

  const openCount = countOpenDeals(deals);
  const selectedBorrowerDeal = deals.find((d) => d.id === selectedDealId) || null;
  const selectedPartyDeal = !selectedBorrowerDeal
    ? relatedPartyDeals.find((d) => d.id === selectedDealId) || null
    : null;
  const selectedDeal = selectedBorrowerDeal || selectedPartyDeal;

  const handleMenuOpen = () => {
    setFilterText('');
    onOpen();
    // Chakra focuses the MenuList on open — steal focus back for typing
    if (showSearch) {
      setTimeout(() => searchInputRef.current?.focus(), 60);
    }
  };

  const handleSelect = (dealId) => {
    onSelect(dealId);
    onClose();
  };

  const handleSearchKeyDown = (e) => {
    // Let Escape bubble so the menu closes; keep everything else out of
    // Chakra's menu typeahead/arrow-key handling
    if (e.key === 'Escape') return;
    e.stopPropagation();
    if (e.key === 'Enter') {
      const first = filteredActive[0] || filteredClosed[0] || filteredParty[0];
      if (first) handleSelect(first.id);
    }
  };

  const handleSearchBlur = (e) => {
    // Chakra's useMenuItem focuses the MenuList container whenever an item
    // mounts while no item is focused — e.g. deals reappearing after the
    // term is corrected. Blurring to the container is never a user action
    // (users click items or outside), so take focus back. Deferred: this blur
    // fires inside Chakra's focus() call, and a synchronous refocus would be
    // overwritten when that call completes.
    if (e.relatedTarget?.getAttribute?.('role') === 'menu') {
      const input = e.target;
      setTimeout(() => input.focus(), 0);
    }
  };

  const handleMenuListFocus = (e) => {
    // By the time the container's focus event fires, focus has already been
    // applied, so redirecting to the search box here is synchronous and
    // leaves no gap where a keystroke could land on the menu itself. No-op
    // when the search box isn't rendered (few deals).
    if (e.target === e.currentTarget) {
      searchInputRef.current?.focus();
    }
  };

  return (
    <Box pt={compact ? 0 : 2} pb={compact ? 0 : 2} w="100%">
      <HStack spacing={2} py={compact ? 0 : 1} flexWrap="wrap">
        {!compact && (
          <HStack spacing={1} flexShrink={0} color={mutedColor} pr={1}>
            <Icon as={FiBriefcase} boxSize={3.5} />
            <Text fontSize="xs" fontWeight="semibold" textTransform="uppercase">
              Deals
            </Text>
            {openCount >= 2 && (
              <Tooltip label="This person has multiple open deals (two-pack) — switch between them here">
                <Badge colorScheme="orange" fontSize="2xs">{openCount} active</Badge>
              </Tooltip>
            )}
          </HStack>
        )}

        {loading && !hasAnyDeal && <Spinner size="xs" color="blue.400" />}

        {!loading && !hasAnyDeal && (
          <>
            <Badge colorScheme="gray" variant="subtle" flexShrink={0}>
              No deals yet — contact-only mode
            </Badge>
            <Box
              role="button"
              tabIndex={0}
              onClick={onNewDeal}
              onKeyDown={(e) => {
                if (e.key === 'Enter') onNewDeal();
              }}
              flexShrink={0}
              px={3}
              py={1.5}
              borderWidth="2px"
              borderStyle="dashed"
              borderColor={dashedBorder}
              borderRadius="lg"
              cursor="pointer"
              transition="border-color 0.15s"
              _hover={{ borderColor: newDealColor }}
            >
              <HStack spacing={1} color={newDealColor}>
                <Icon as={FiPlus} boxSize={3.5} />
                <Text fontSize="xs" fontWeight="semibold" whiteSpace="nowrap">
                  New Deal
                </Text>
              </HStack>
            </Box>
          </>
        )}

        {hasAnyDeal && (
          <Menu autoSelect={false} isOpen={isOpen} onOpen={handleMenuOpen} onClose={onClose}>
            <MenuButton
              as={Box}
              role="button"
              tabIndex={0}
              px={3}
              py={1.5}
              bg={buttonBG}
              borderWidth="2px"
              borderColor={buttonBorder}
              borderRadius="lg"
              cursor="pointer"
              transition="background 0.15s"
              _hover={{ bg: buttonHoverBG }}
              minW={compact ? 0 : { base: '0', md: '320px' }}
              maxW={compact ? '100%' : '560px'}
              flex={compact ? 1 : undefined}
            >
              {selectedDeal ? (
                <HStack spacing={2} justify="space-between">
                  <VStack spacing={0.5} align="flex-start" minW={0}>
                    <HStack spacing={2} minW={0}>
                      <StageBadge stage={selectedDeal.stage} />
                      <Text fontSize="sm" fontWeight="bold" noOfLines={1} minW={0}>
                        {dealDisplayLabel(selectedDeal)}
                      </Text>
                      {formatChipAmount(selectedDeal.amount) && (
                        <Text fontSize="xs" fontWeight="bold" color="green.500" flexShrink={0}>
                          {formatChipAmount(selectedDeal.amount)}
                        </Text>
                      )}
                      {selectedPartyDeal && (
                        <Badge colorScheme="cyan" fontSize="2xs" flexShrink={0}>
                          Party · {formatRole(selectedPartyDeal.relationshipRole)}
                        </Badge>
                      )}
                      {selectedDeal.ariveId && (
                        <Badge colorScheme="teal" fontSize="2xs" flexShrink={0}>Arive</Badge>
                      )}
                    </HStack>
                    {dealSubtext(selectedDeal, { isParty: Boolean(selectedPartyDeal) }) && (
                      <Text fontSize="2xs" color={mutedColor} noOfLines={1}>
                        {dealSubtext(selectedDeal, { isParty: Boolean(selectedPartyDeal) })}
                      </Text>
                    )}
                  </VStack>
                  <Icon as={FiChevronDown} color={mutedColor} flexShrink={0} />
                </HStack>
              ) : (
                <HStack spacing={2} justify="space-between">
                  <Text fontSize="sm" fontWeight="semibold" color={mutedColor}>
                    Select a deal…
                  </Text>
                  <Icon as={FiChevronDown} color={mutedColor} />
                </HStack>
              )}
            </MenuButton>

            <MenuList
              zIndex={120}
              maxW="560px"
              py={1}
              maxH="440px"
              overflowY="auto"
              onFocus={handleMenuListFocus}
            >
              {showSearch && (
                <Box px={3} pt={1.5} pb={2}>
                  <InputGroup size="sm">
                    <InputLeftElement pointerEvents="none">
                      <Icon as={FiSearch} color="gray.400" boxSize={3.5} />
                    </InputLeftElement>
                    <Input
                      ref={searchInputRef}
                      value={filterText}
                      onChange={(e) => setFilterText(e.target.value)}
                      onKeyDown={handleSearchKeyDown}
                      onBlur={handleSearchBlur}
                      placeholder="Search borrower, address, or loan #…"
                      borderRadius="md"
                    />
                  </InputGroup>
                </Box>
              )}

              {filteredActive.length > 0 && (
                <MenuGroup title={`Active (${filteredActive.length})`}>
                  {filteredActive.map((deal) => (
                    <DealMenuItem
                      key={deal.id}
                      deal={deal}
                      isSelected={deal.id === selectedDealId}
                      isParty={false}
                      onSelect={handleSelect}
                    />
                  ))}
                </MenuGroup>
              )}

              {filteredClosed.length > 0 && (
                <>
                  {filteredActive.length > 0 && <MenuDivider my={1} />}
                  <MenuGroup title={`Closed / Canceled (${filteredClosed.length})`}>
                    {filteredClosed.map((deal) => (
                      <DealMenuItem
                        key={deal.id}
                        deal={deal}
                        isSelected={deal.id === selectedDealId}
                        isParty={false}
                        onSelect={handleSelect}
                      />
                    ))}
                  </MenuGroup>
                </>
              )}

              {filteredParty.length > 0 && (
                <>
                  {(filteredActive.length > 0 || filteredClosed.length > 0) && <MenuDivider my={1} />}
                  {/* Deals this person is on as agent/co-borrower/title/etc —
                      selectable like any other, so you can click through every
                      deal you share with this person */}
                  <MenuGroup title={`Party (${filteredParty.length})`}>
                    {filteredParty.map((deal) => (
                      <DealMenuItem
                        key={deal.id}
                        deal={deal}
                        isSelected={deal.id === selectedDealId}
                        isParty
                        onSelect={handleSelect}
                      />
                    ))}
                  </MenuGroup>
                </>
              )}

              {noMatches && (
                <Box px={4} py={3}>
                  <Text fontSize="sm" color={mutedColor}>
                    No deals match &ldquo;{filterText.trim()}&rdquo;
                  </Text>
                </Box>
              )}

              <MenuDivider my={1} />

              {/* + New Deal — covers the second home / duplex / backup-deal cases */}
              <MenuItem
                onClick={() => {
                  onClose();
                  onNewDeal();
                }}
                py={2}
              >
                <HStack spacing={2} color={newDealColor} pl={6}>
                  <Icon as={FiPlus} boxSize={4} />
                  <Text fontSize="sm" fontWeight="semibold">
                    New Deal
                  </Text>
                </HStack>
              </MenuItem>
            </MenuList>
          </Menu>
        )}
      </HStack>
    </Box>
  );
}
